// src/modules/pedidos/services/recuentosFS.js
//
// Servicio de "Recuentos de stock" (conteo cíclico).
//  - nus_importados: biblioteca de NUs (packing lists) ya parseados.
//  - recuentos: cada recuento con sus items y el conteo.

import { db } from "../../../firebase";
import {
  collection, addDoc, doc, updateDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";

// ── Biblioteca de NUs ──────────────────────────────────────
export async function guardarNUImportado({ nombre, archivoNombre, productos, deposito, profile }) {
  const ref = await addDoc(collection(db, "nus_importados"), {
    nombre: nombre || "NU sin nombre",
    archivoNombre: archivoNombre || null,
    deposito: deposito || null,
    productos,
    totalProductos: productos.length,
    importadoPorUid: profile?.id || profile?.uid || null,
    importadoPorNombre: profile?.nombre || profile?.email || null,
    importadoAt: serverTimestamp(),
  });
  return ref.id;
}

export async function borrarNUImportado(id) {
  await deleteDoc(doc(db, "nus_importados", id));
}

// ── Recuentos ──────────────────────────────────────────────
// Construye los items a contar a partir de una lista de productos.
// `codigo` = código de Finnegans (codUru); `mold` = código del proveedor (NU), opcional.
export function itemsDesdeProductos(productos) {
  return productos.map((p, idx) => ({
    idx,
    codigo: p.codUru || p.codigo || "",
    mold: p.mold || "",
    nombre: p.nombre || p.customerNo || p.descripcion || "",
    serie: p.serie || p.rama1 || "",
    color: p.color || "—",
    tirasPorPaquete: Number(p.tirasPorPaquete || p.pieces || p.bundles || 0) || 0,
    grupos: [],        // [{ paquetes, tirasPorPaquete }]
    sueltas: 0,
    total: 0,
    contado: false,
    contadoPorNombre: null,
    contadoAt: null,
  }));
}

export async function crearRecuento({ nombre, motivo, deposito, origen, nuId, nuNombre, productos, asignadoA, profile }) {
  const items = itemsDesdeProductos(productos);
  const ref = await addDoc(collection(db, "recuentos"), {
    nombre: nombre || "Recuento",
    motivo: motivo || null,
    deposito: deposito || null,
    origen: origen || "NU",
    nuId: nuId || null,
    nuNombre: nuNombre || null,
    estado: "ASIGNADO",
    items,
    totalProductos: items.length,
    contados: 0,
    asignadoAUid: asignadoA?.uid || null,
    asignadoANombre: asignadoA?.nombre || null,
    creadoPorUid: profile?.id || profile?.uid || null,
    creadoPorNombre: profile?.nombre || profile?.email || null,
    creadoAt: serverTimestamp(),
  });
  return ref.id;
}

// Calcula el total de tiras de un item según sus grupos + sueltas.
export function totalItem(it) {
  const grupos = Array.isArray(it.grupos) ? it.grupos : [];
  const dePaquetes = grupos.reduce((s, g) => s + (Number(g.paquetes) || 0) * (Number(g.tirasPorPaquete) || 0), 0);
  return dePaquetes + (Number(it.sueltas) || 0);
}

// Guarda el array completo de items. Pasá `nuevoEstado` para cambiar el estado
// (ej. ASIGNADO → EN_CURSO al guardar el primer conteo).
export async function guardarItems(recuentoId, items, nuevoEstado) {
  const contados = items.filter(it => it.contado).length;
  const patch = { items, contados, actualizadoAt: serverTimestamp() };
  if (nuevoEstado) patch.estado = nuevoEstado;
  await updateDoc(doc(db, "recuentos", recuentoId), patch);
}

// El operario (o quien cuenta) marca el recuento como TERMINADO → espera confirmación.
export async function marcarTerminado(recuentoId, items, profile) {
  const contados = items.filter(it => it.contado).length;
  await updateDoc(doc(db, "recuentos", recuentoId), {
    items,
    contados,
    estado: "TERMINADO",
    terminadoAt: serverTimestamp(),
    terminadoPorNombre: profile?.nombre || profile?.email || null,
  });
}

// El encargado/admin confirma el recuento terminado.
export async function confirmarRecuento(recuentoId, profile) {
  await updateDoc(doc(db, "recuentos", recuentoId), {
    estado: "CONFIRMADO",
    confirmadoAt: serverTimestamp(),
    confirmadoPorNombre: profile?.nombre || profile?.email || null,
  });
}

// Reabrir para seguir/rehacer el conteo.
export async function reabrirRecuento(recuentoId) {
  await updateDoc(doc(db, "recuentos", recuentoId), { estado: "EN_CURSO" });
}

export async function asignarRecuento(recuentoId, asignadoA) {
  await updateDoc(doc(db, "recuentos", recuentoId), {
    asignadoAUid: asignadoA?.uid || null,
    asignadoANombre: asignadoA?.nombre || null,
  });
}

export async function borrarRecuento(id) {
  await deleteDoc(doc(db, "recuentos", id));
}
